import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "./Logo";
import { CHAT_STRINGS } from "../i18n/chatStrings";
import { WHATSAPP_URL } from "../i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatWidget = ({ lang }) => {
  const s = CHAT_STRINGS[lang] || CHAT_STRINGS.es;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [leadMode, setLeadMode] = useState(false);
  const [lead, setLead] = useState({ name: "", contact: "" });
  const scrollRef = useRef(null);

  const sessionId = useMemo(() => {
    let id = sessionStorage.getItem("sagas_chat_session");
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem("sagas_chat_session", id);
    }
    return id;
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: s.welcome }]);
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, lang }),
      });
      if (!res.ok || !res.body) throw new Error();
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let failed = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") continue;
          const parsed = JSON.parse(payload);
          if (parsed.error) {
            failed = true;
            continue;
          }
          if (parsed.text) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + parsed.text };
              return copy;
            });
          }
        }
      }
      if (failed) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: s.error };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: s.error };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.contact.trim()) return;
    const isEmail = lead.contact.includes("@");
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: lead.name,
          email: isEmail ? lead.contact : "pendiente@sagaslogistic.com",
          phone: isEmail ? null : lead.contact,
          message: "Lead capturado desde el asistente virtual",
          lang,
          source: "chatbot",
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(s.leadDone);
      setMessages((m) => [...m, { role: "assistant", text: s.leadDone }]);
      setLead({ name: "", contact: "" });
      setLeadMode(false);
    } catch {
      toast.error(s.error);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen(!open)}
        aria-label={s.title}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, duration: 0.5, ease: "backOut" }}
        whileHover={{ scale: 1.08 }}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full border border-amber-400/40 bg-[#121722] text-amber-400 shadow-[0_8px_30px_rgba(229,169,59,0.3)] transition-colors duration-300 hover:bg-[#1A202C]"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-50 flex h-[540px] max-h-[72vh] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0C1018]/95 shadow-2xl backdrop-blur-xl sm:right-6"
          >
            <div className="flex items-center gap-3 border-b border-white/10 bg-[#121722] px-4 py-3.5">
              <LogoMark size={32} />
              <div className="flex-1 leading-tight">
                <div data-testid="chat-title" className="font-display text-sm font-bold text-white">{s.title}</div>
                <div className="flex items-center gap-1.5 text-[11px] text-emerald-400">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                  {s.subtitle}
                </div>
              </div>
              <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="text-slate-400 transition-colors hover:text-white" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-message-${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-sm bg-amber-400 text-slate-950"
                        : "rounded-bl-sm border border-white/10 bg-[#161C28] text-slate-200"
                    }`}
                  >
                    {m.text || (m.role === "assistant" && sending && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : null)}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 border-t border-white/10 px-4 pt-3">
              <a
                data-testid="chat-quick-whatsapp"
                href={`${WHATSAPP_URL}?text=Hola%20Sagas%20Logistic`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-emerald-400/40 px-3.5 py-1.5 text-xs font-semibold text-emerald-300 transition-colors hover:bg-emerald-400/10"
              >
                {s.whatsapp}
              </a>
              <button
                data-testid="chat-quick-lead"
                onClick={() => setLeadMode(!leadMode)}
                className="rounded-full border border-amber-400/40 px-3.5 py-1.5 text-xs font-semibold text-amber-300 transition-colors hover:bg-amber-400/10"
              >
                {s.lead}
              </button>
            </div>

            {leadMode && (
              <form data-testid="chat-lead-form" onSubmit={submitLead} className="space-y-2 px-4 pt-3">
                <input
                  data-testid="chat-lead-name-input"
                  value={lead.name}
                  onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  placeholder={s.leadName}
                  className="w-full rounded-lg border border-white/10 bg-[#0A0D14] px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-400/60"
                />
                <div className="flex gap-2">
                  <input
                    data-testid="chat-lead-contact-input"
                    value={lead.contact}
                    onChange={(e) => setLead({ ...lead, contact: e.target.value })}
                    placeholder={s.leadContact}
                    className="flex-1 rounded-lg border border-white/10 bg-[#0A0D14] px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-400/60"
                  />
                  <button data-testid="chat-lead-submit" type="submit" className="rounded-lg bg-amber-400 px-4 text-sm font-bold text-slate-950 transition-colors hover:bg-amber-300">
                    {s.leadSubmit}
                  </button>
                </div>
              </form>
            )}

            <div className="flex items-center gap-2 p-4">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={s.placeholder}
                className="flex-1 rounded-full border border-white/10 bg-[#0A0D14] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-400/60"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={sending || !input.trim()}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-400 text-slate-950 transition-colors hover:bg-amber-300 disabled:opacity-50"
                aria-label="Send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
